import { useId } from "react";
import { ImagePlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import type { ImageKind, ImageUploader } from "../useImageUpload";

type Props = {
  kind: ImageKind;
  label: string;
  url: string;
  images: ImageUploader;
  onClear: () => void;
  showDropzoneHint?: boolean;
};

export function ImageField({ kind, label, url, images, onClear, showDropzoneHint }: Props) {
  const { t } = useTranslation("settings");
  const id = useId();
  const src = images.previews[kind] ?? (url || null);
  const name = images.previews[kind] ? t("printEditor.header.image") : url.split("/").pop() ?? "";
  const input = (
    <input id={`${id}-file`} type="file" accept="image/*" className="sr-only" onChange={images.onInputChange(kind)} />
  );

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${id}-file`}>{label}</Label>
      {src ? (
        <div className="flex items-center gap-3">
          <img src={src} alt="" className="h-10 w-16 rounded-md border bg-card object-contain" />
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{name}</span>
          <Button asChild type="button" variant="outline" size="sm">
            <label htmlFor={`${id}-file`} className="cursor-pointer">
              {t("printEditor.header.change")}
            </label>
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            {t("printEditor.header.remove")}
          </Button>
          {input}
        </div>
      ) : (
        <label
          htmlFor={`${id}-file`}
          className="flex cursor-pointer flex-col items-center gap-1 rounded-xl border-[1.5px] border-dashed p-[18px] text-center focus-within:ring-2 focus-within:ring-ring hover:bg-secondary"
        >
          <ImagePlus aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
          <span className="text-[13px] font-medium">{t("printEditor.paper.dropzone")}</span>
          {showDropzoneHint ? <span className="text-xs text-muted-foreground">{t("printEditor.paper.dropzoneHint")}</span> : null}
          {input}
        </label>
      )}
    </div>
  );
}
